import { useEffect, useId, useRef, useState } from 'react';
import type { ParseKeys } from 'i18next';
import {
  MICE_PROFILE_FIELDS,
  type MiceProfileCheckView,
  type MiceProfileField,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button } from '@web/ui/core/atoms';
import { ToneChip, type ToneChipTone } from '@web/ui/core/molecules';
import { useProfileHistory, useProfileResolve } from '../hooks/use_profile_actions';
import {
  EMPTY_DRAFT,
  draftBody,
  nameTyped,
  rowMode,
  type ProfileBlockReason,
  type ProfileDraft,
} from '../travelers/profile_draft';
import { ActivityItem } from './activity_feed';
import { ProfileFieldRow } from './profile_fields';

const STATUS_CHIP = {
  ok: { tone: 'green', key: 'mice.profile.status.ok' },
  pending: { tone: 'amber', key: 'mice.profile.status.pending' },
  incomplete: { tone: 'red', key: 'mice.profile.status.incomplete' },
} as const satisfies Record<MiceProfileCheckView['status'], { tone: ToneChipTone; key: ParseKeys }>;

const REASON = {
  name: 'mice.profile.reason.name',
  birthdate: 'mice.profile.reason.birthdate',
  cpf: 'mice.profile.reason.cpf',
  gender: 'mice.profile.reason.gender',
  choose: 'mice.profile.reason.choose',
  fill: 'mice.profile.reason.fill',
} as const satisfies Record<ProfileBlockReason, ParseKeys>;

/** "Gravar", com o motivo ao lado enquanto ainda não pode ir. */
function SaveBar({
  reason,
  pending,
  onSave,
}: Readonly<{ reason: ProfileBlockReason | null; pending: boolean; onSave: () => void }>) {
  const reasonId = useId();
  const shown = reason !== null && !pending;
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {shown ? (
        <span id={reasonId} className="text-micro text-muted-foreground">
          {t(REASON[reason])}
        </span>
      ) : null}
      <Button
        size="sm"
        variant="primary"
        disabled={pending || reason !== null}
        aria-describedby={shown ? reasonId : undefined}
        onClick={onSave}
      >
        {t('mice.profile.save')}
      </Button>
    </div>
  );
}

export interface ProfileSectionProps {
  projectId: string;
  /** `MiceTraveler.id`: `need.id` ou `order:<id>`. */
  travelerId: string;
  travelerEmail: string;
  check: MiceProfileCheckView;
  /** `mice:purchase`: decidir e digitar; sem ela, só os valores. */
  canResolve: boolean;
  /** Aberto por "Completar cadastro": a seção recebe o foco e entra na tela. */
  focus: boolean;
}

function ProfileHistory({
  projectId,
  travelerEmail,
}: Readonly<{ projectId: string; travelerEmail: string }>): React.JSX.Element {
  const { events, isError } = useProfileHistory(projectId, travelerEmail);
  const title = t('mice.profile.history');
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-micro font-medium text-muted-foreground">{title}</span>
      {isError ? <p className="text-meta text-destructive">{t('mice.activity.error')}</p> : null}
      {!isError && events.length === 0 ? (
        <p className="text-meta text-muted-foreground">{t('mice.profile.historyEmpty')}</p>
      ) : null}
      {events.length > 0 ? (
        <ol aria-label={title} className="flex flex-col gap-1.5">
          {events.map((e) => (
            <ActivityItem key={e.id} event={e} withDate />
          ))}
        </ol>
      ) : null}
    </div>
  );
}

/**
 * Seção Cadastro do painel do viajante (spec B4): uma linha por dado com a planilha, a VOLL e o
 * estado; a decisão de cada campo e o formulário do que falta vão juntos em "Gravar". O erro da
 * rota aparece aqui, e o histórico traz as decisões já registradas.
 */
export function ProfileSection(props: Readonly<ProfileSectionProps>): React.JSX.Element {
  const { check } = props;
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [draft, setDraft] = useState<ProfileDraft>(EMPTY_DRAFT);
  const { resolve, pending, error } = useProfileResolve(props.projectId);

  useEffect(() => {
    if (!props.focus) return;
    headingRef.current?.scrollIntoView({ block: 'start' });
    headingRef.current?.focus();
  }, [props.focus]);

  const { body, reason } = draftBody(check, draft);
  const actionable = MICE_PROFILE_FIELDS.some((f) => rowMode(check, f) !== 'none');
  const typingOf = (field: MiceProfileField): boolean =>
    field === 'name' ? nameTyped(check, draft) : rowMode(check, field) === 'type';
  const chip = STATUS_CHIP[check.status];

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-faint-foreground focus:outline-none"
        >
          {t('mice.profile.title')}
        </h3>
        <ToneChip tone={chip.tone}>{t(chip.key)}</ToneChip>
      </div>
      {MICE_PROFILE_FIELDS.map((field) => (
        <ProfileFieldRow
          key={field}
          field={field}
          view={check.fields[field]}
          mode={rowMode(check, field)}
          canResolve={props.canResolve}
          typing={typingOf(field)}
          draft={draft}
          onDraft={(next) => {
            setDraft((current) => ({ ...current, ...next }));
          }}
        />
      ))}
      {error ? (
        <p role="alert" className="text-meta text-destructive">
          {error}
        </p>
      ) : null}
      {props.canResolve && actionable ? (
        <SaveBar
          reason={reason}
          pending={pending}
          onSave={() => {
            if (!body) return;
            resolve(props.travelerId, body, {
              onDone: () => {
                setDraft(EMPTY_DRAFT);
              },
              // O nome da planilha veio inteiro (malha): abre Nome e Sobrenome vazios.
              onNameSplit: () => {
                setDraft((current) => ({ ...current, nameSplit: true }));
              },
            });
          }}
        />
      ) : null}
      <ProfileHistory projectId={props.projectId} travelerEmail={props.travelerEmail} />
    </section>
  );
}
